import {
  TableRow,
  TableCell,
  Stack,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

import { getMeetingStatus } from "../../../types-helpers";
import { Meeting, MeetingStatus, Organisation, User } from "../../../types";
import { getCalendarMeetingTypeLabel } from "../../../components/calendar/calendar-status-utils";
import AttendeeList from "../../../components/attendees/attendee-list";
import UserChip from "../../../components/chips/user-chip";
import { getAttendeeStatus } from "../../../components/attendees/utils";

interface PatientMeetingsTableRowProps {
  meeting: Meeting;
  users: Record<string, User>;
  teams: Record<string, Organisation>;
  openEdit: (item: Meeting) => void;
  deleteButtonClick: (item: Meeting) => void;
}

const PatientMeetingsTableRow: React.FC<PatientMeetingsTableRowProps> = ({
  meeting,
  teams,
  users,
  openEdit,
  deleteButtonClick,
}) => {
  const theme = useTheme();

  const getUserName = (userId: string) => {
    return users[userId]?.fullName || "Unknown User";
  };

  const formatDate = (dateTime: string | Date) => {
    const date = new Date(dateTime);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateTime: string | Date) => {
    const date = new Date(dateTime);

    const seconds = date.getSeconds();
    if (seconds >= 30) {
      date.setMinutes(date.getMinutes() + 1);
    }

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = hours.toString();

    return `${hoursStr}:${minutes} ${ampm}`;
  };

  const { status } = getMeetingStatus(meeting);
  const paletteEntry = theme.palette.meetingStatus[status];

  const selectedUsers = (meeting.attendeeIds || []).map((attendeeId) => {
    const attendeeUser = users[attendeeId];
    const attendeeStatus = getAttendeeStatus(
      status,
      meeting.joinedInfo?.find((joinInfo) => joinInfo.userId === attendeeId),
    );

    return {
      userId: attendeeId,
      label: attendeeUser?.fullName || attendeeUser?.email || "Unknown User",
      teams: (attendeeUser?.organisationIds || [])
        .map((teamId) => ({
          teamId,
          name: teams[teamId]?.name || "Unknown Organisation",
          color: teams[teamId]?.color,
        }))
        .filter((team) => team.name),
      status: attendeeStatus,
    };
  });

  const selectedTeams = (meeting.organisationIds || []).map(
    (teamId) => ({
      teamId,
      name: teams[teamId]?.name || "Unknown Team",
      color: teams[teamId]?.color,
    }),
  );

  return (
    <TableRow
      key={meeting.meetingId}
      sx={{
        cursor: "pointer",
        "&:hover": {
          background:
            "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
        },
      }}
      onClick={() => openEdit(meeting)}
    >
      <TableCell align="center" sx={{ py: 1, width: 120, whiteSpace: "nowrap" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
            backgroundColor: paletteEntry?.main ?? "#717172",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              fontWeight: 600,
              lineHeight: 1,
              fontSize: "0.7rem",
            }}
          >
            {getCalendarMeetingTypeLabel(status)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ py: 1, width: 150, whiteSpace: "nowrap" }}>
        <Stack direction="column" spacing={0.5} alignItems="center">
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            {formatDate(meeting.startTime)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {formatTime(meeting.startTime)}
            </Typography>
            {meeting.realStartTime && (
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: "medium" }}
              >
                {formatTime(meeting.realStartTime)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </TableCell>
      <TableCell align="center" sx={{ py: 1, width: 150, whiteSpace: "nowrap" }}>
        <Stack direction="column" spacing={0.5} alignItems="center">
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            {formatDate(meeting.endTime)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {formatTime(meeting.endTime)}
            </Typography>
            {meeting.realEndTime && (
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: "medium" }}
              >
                {formatTime(meeting.realEndTime)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </TableCell>
      <TableCell sx={{ py: 1, width: 220, whiteSpace: "nowrap" }}>
        <UserChip name={meeting.creatorId ? getUserName(meeting.creatorId) : "Unknown User"} />
      </TableCell>
      <TableCell sx={{ py: 1, width: "100%", minWidth: 260 }}>
        <AttendeeList
          selectedUsers={selectedUsers}
          selectedInviteEmails={[]}
          selectedTeams={selectedTeams}
          editable={false}
        />
      </TableCell>
      <TableCell align="right" sx={{ py: 1, width: 96, whiteSpace: "nowrap" }}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <IconButton
            size="small"
            title="Cancel Meeting"
            color="error"
            sx={{ padding: "4px" }}
            disabled={status != MeetingStatus.Scheduled}
            onClick={(e) => {
              e.stopPropagation();
              deleteButtonClick(meeting);
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default PatientMeetingsTableRow;
